import React, { useMemo } from "react";
import { Button, Col, Container, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { MinusIcon, PlusIcon, TimesIcon } from "@simplex/sx-icons";
import { CreateGUID } from "@common/helpers/funlib";
import { useAppSelector, useAppDispatch } from "../app/hooks";

import {
  SXEditField,
  removeInvalidTextFromNumberString,
  UnitCreator,
} from "@common/components/ui-components/SXEditField";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import { t } from "i18next";
import { addSup, addStageSup, getNode } from "@common/helpers/protoGen";
import { classifySupport, SupportAxis } from "@common/helpers/sharedTypes";
import { AlertMessage } from "../helpers/AlertMessage";
import { ng } from "@common/ngds";
import { useTranslation } from "react-i18next";
import { useProject, SupportConnectivityType } from "../features/projects/projectSlice";
import { updateProject } from "../features/ngds/ngdsSlice";
import { useUnit } from "../features/unit/unitSlice";


// --- Main component ---

interface ProjectInputGeometryProps {
  units?: {
    makeConverter: (unit: string) => any;
  };
}

export const SXInputGeometry = (props: ProjectInputGeometryProps) => {
  const { t, i18n } = useTranslation();

  const { lengthUnit, toDisplayLength, fromDisplayLength } = useUnit();

  // Dummy units converter for demo
  const dummyUnits = {
    makeConverter: (unit: string) => unit,
  };
  // Use dummy units if not provided
  const units = props.units || dummyUnits;
  const {
    project: selProject,
    setColumnLength,
    addSupport,
    deleteSupport,
    updateSupportPosition,
    updateSupportConnectivity,
  } = useProject();

  const length = useMemo(
    () => (selProject?.data.data?.input?.structures[0]?.elements[0].beam?.segments[0].length as number) || 0,
    [selProject],
  );
  const sups = useMemo(() => selProject?.data.data?.input?.structures[0].stages[0].sups || [], [selProject]);

  // Connectivity options for dropdowns
  const FIRST_ROW_CONNECTIVITY = [
    { id: "rigid", name: "rigid_.label", label: "Rigid" },
    { id: "pinned", name: "pinned_.label", label: "Pinned" },
    { id: "rigid_roller", name: "rigid_roller_.label", label: "Rigid-roller" },
  ];
  const OTHER_ROW_CONNECTIVITY = [
    { id: "roller", name: "roller_.label", label: "Roller" },
    { id: "free", name: "free_.label", label: "Free" },
    { id: "rigid_roller", name: "rigid_roller_.label", label: "Rigid-roller" },
  ];

  // Dummy handlers
  const _updateLength = (val: string, flag: boolean) => true;
  const _updatePos = (guid: string, value: number) => {
    updateSupportPosition(guid, value);
  };
  const _updateSW = (value: number) => { };
  const _delSup = (guid: string) => {
    deleteSupport(guid);
  };

  const _addSup = () => {
    // --- Determine Smart X Coordinate ---
    const filteredSups = sups.filter((s: ng.stage.Support_json) => !s.lateral);
    const supsPositions = filteredSups.map((s: ng.stage.Support_json) => {
      const support = ng.stage.Support.fromObject(s);
      return support.getCoord()?.x ?? 0;
    });
    let newX = 0;

    // Helper to check if a position is approximately taken (within 1mm)
    const isPositionTaken = (pos: number) => supsPositions.some((p) => Math.abs(p - pos) < 0.001);

    if (!isPositionTaken(0)) {
      newX = 0;
    } else if (!isPositionTaken(length)) {
      newX = length;
    } else {
      // Pick a x value in the middle of the biggest current span
      const sortedPositions = [...supsPositions].sort((a, b) => a - b);
      let maxSpan = 0;
      let bestMidPoint = 0;

      for (let i = 0; i < sortedPositions.length - 1; i++) {
        const span = sortedPositions[i + 1] - sortedPositions[i];
        if (span > maxSpan) {
          maxSpan = span;
          bestMidPoint = sortedPositions[i] + span / 2;
        }
      }
      newX = bestMidPoint;
    }

    // Final safety: Ensure 1mm precision and uniqueness
    newX = Math.round(newX * 1000) / 1000;
    while (isPositionTaken(newX)) {
      newX += 0.001;
      newX = Math.round(newX * 1000) / 1000;
    }

    const supportGUid = CreateGUID();
    const support = new ng.stage.Support();
    support.guid = supportGUid;
    support.lateral = false;
    support.setCoord(ng.geometry.Point3D.create({ x: newX, y: 0.0, z: 0.0 }));
    support.con = ng.topology.Connectivity.create({
      transX: ng.topology.Stiffness.create({ value: -1 }),
      transY: ng.topology.Stiffness.create({ value: -1 }),
      transZ: ng.topology.Stiffness.create({ value: -1 }),
      rotX: ng.topology.Stiffness.create({ value: 0 }),
      rotY: ng.topology.Stiffness.create({ value: 0 }),
      rotZ: ng.topology.Stiffness.create({ value: 0 }),
    });

    const supportjson = ng.stage.Support.toObject(support) as ng.stage.Support_json;
    if (supportjson != null) {
      addSupport(supportjson);
    }
  };

  // --- Render ---

  return (
    <Container fluid id="sx-table-container">
      <Row className="align-items-center">
        <Col className="ps-0 px-0">
          <Container>
            <Row className="container-row">
              <Col className="editfield-label">
                {t("height_.label")} ({lengthUnit})
              </Col>
              <Col xs={12} className="ps-0 px-0" style={{ height: 32 }}>
                <SXEditField
                  fullWidth
                  textAlign="left"
                  id={"columheight"}
                  fontSize={12}
                  type="number"
                  disableNegativeNumber={true}
                  val={toDisplayLength(length || 0)}
                  onUpdate={(newValue: number, revert: () => void) => {
                    if (!Number.isFinite(newValue) || newValue <= 0) {
                      AlertMessage(
                        "error_",
                        t("error_.label"),
                        "",
                        t("MSG_value_must_be_greater_than_.label") + " 0 " + lengthUnit,
                      );
                      // Only revert when there is a valid value to fall back to,
                      // otherwise revert() would re-trigger this same validation.
                      if (length > 0) {
                        revert();
                      }
                      return;
                    }
                    const filteredSups = sups.filter(
                      (sup: ng.stage.Support_json) => !sup.lateral && (sup.coord?.x as number) > 0.0,
                    );
                    if (filteredSups.length > 0) {
                      setColumnLength(newValue, filteredSups[0] as { guid: string });
                    } else {
                      setColumnLength(newValue);
                    }
                  }}
                />
              </Col>
            </Row>
          </Container>

          <Row>
            <Col xs={11} style={{ marginBottom: 5 }}>
              <span className="header">{t("supports_.label")}</span>
            </Col>
          </Row>

          <Container className="sx-table-container">
            <Table hover size="sm" className="main-sx-table">
              <thead>
                <tr>
                  <th>
                    {t("support_position_.label")} ({lengthUnit})
                  </th>
                  <th>{t("connectivity_.label")}(y)</th>
                  <th>{t("connectivity_.label")}(z)</th>
                  <th style={{ border: 0, width: "10%" }}></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filteredSups = sups.filter((sup: ng.stage.Support_json) => !sup.lateral);

                  // Sort supports by X coordinate (highest to lowest)
                  const sortedSups = [...filteredSups].sort((a, b) => {
                    const supA = ng.stage.Support.fromObject(a);
                    const supB = ng.stage.Support.fromObject(b);
                    return (supB.getCoord()?.x ?? 0) - (supA.getCoord()?.x ?? 0);
                  });

                  const minX = Math.min(
                    ...filteredSups.map((s: ng.stage.Support_json) => {
                      const support = ng.stage.Support.fromObject(s);
                      return support.getCoord()?.x ?? 0;
                    }),
                  );

                  return sortedSups.map((supJson: ng.stage.Support_json, index: number) => {
                    const sup = ng.stage.Support.fromObject(supJson);
                    const currentX = sup.getCoord()?.x ?? 0;
                    const isBaseSupport = currentX === minX;
                    const dropdownOptions = isBaseSupport ? FIRST_ROW_CONNECTIVITY : OTHER_ROW_CONNECTIVITY;

                    const getDropdownValue = (sup: ng.stage.Support, axis: SupportAxis) => {
                      const type = classifySupport(sup, axis, isBaseSupport);
                      return dropdownOptions.find((op) => op.id === type);
                    };

                    const checkSupportTypes = (
                      testType: SupportConnectivityType,
                      axis: SupportAxis,
                      silent = false,
                    ) => {

                      let compSup = ng.stage.Support.fromObject(sups[0]); // base support
                      if (isBaseSupport) {
                        compSup = ng.stage.Support.fromObject(sups[1]); // top support
                      }
                      const compType = classifySupport(compSup, axis, !isBaseSupport);

                      // combination not allowed:
                      // roller + rigid_roller
                      // free + rigid_roller
                      // free + pinned
                      if (
                        (testType === "roller" && compType === "rigid_roller") ||
                        (testType === "rigid_roller" && compType === "roller") ||
                        (testType === "free" && compType === "rigid_roller") ||
                        (testType === "rigid_roller" && compType === "free") ||
                        (testType === "free" && compType === "pinned") ||
                        (testType === "pinned" && compType === "free")
                      ) {
                        if (!silent) {
                          AlertMessage("error_", t("error_.label"), "", t("unstable_support_combination_.label"));
                        }
                        return false;
                      }

                      return true;
                    }

                    return (
                      <tr
                        key={`supportRow_${index}`}
                        id={`supportRow_${index}`}
                        className="table-body align-items-center"
                      >
                        <td className="text-center">
                          <SXEditField
                            type="number"
                            id={"suppos_" + index + "_pos"}
                            textAlign="left"
                            disabled
                            tableView
                            disableNegativeNumber={true}
                            onUpdate={(value: number) => {
                              if (sup.guid) {
                                const internalX = fromDisplayLength(value);
                                // Ensure unique position (1mm tolerance) and round to 1mm
                                const roundedVal = Math.round(internalX * 1000) / 1000;
                                const isTaken = sups
                                  .filter((s: ng.stage.Support_json) => !s.lateral && s.guid !== sup.guid)
                                  .some((s: ng.stage.Support_json) => {
                                    const otherSup = ng.stage.Support.fromObject(s);
                                    return Math.abs((otherSup.getCoord()?.x ?? 0) - roundedVal) < 0.001;
                                  });

                                if (isTaken) {
                                  console.warn("Support position must be unique.");
                                  return;
                                }

                                console.log("Updating support position for guid", sup.guid, "to", roundedVal);
                                _updatePos(sup.guid, roundedVal);
                              }
                            }}
                            displayUnit={false}
                            val={toDisplayLength(sup.getCoord()?.x ?? 0)}
                          />
                        </td>
                        <td>
                          <SXDropdown
                            tableView
                            name={"sconneY_dropdown_" + index}
                            id={"sconneY_dropdown_" + index}
                            dataTestId={"dropdown_sconneY"}
                            onUpdate={(value: { id: string } | null) => {
                              if (value === null) {
                                return;
                              }

                              const newType = value.id as SupportConnectivityType;

                              if (!checkSupportTypes(newType, "Y")) {
                                return;
                              }

                              if (sup.guid) {
                                // The two directions have the same connectivity for almost all
                                // supports, so choosing a new connectivity(y) sets the same
                                // connectivity(z). Differing directions are made by editing
                                // connectivity(z) afterwards. Only mirror on an actual change,
                                // otherwise re-picking the current y-value would wipe out a
                                // deviating z-value.
                                const yChanged = classifySupport(sup, "Y", isBaseSupport) !== newType;
                                updateSupportConnectivity(sup.guid, "Y", newType);
                                // The user only asked for a change in y, so skip the mirroring
                                // silently if it would give an unstable combination in z.
                                if (yChanged && checkSupportTypes(newType, "Z", true)) {
                                  updateSupportConnectivity(sup.guid, "Z", newType);
                                }
                              }
                            }}
                            list={dropdownOptions}
                            val={getDropdownValue(sup, "Y")}
                            localisation={undefined}
                            lang={""}
                          />
                        </td>
                        <td>
                          <SXDropdown
                            tableView
                            name={"sconneZ_dropdown_" + index}
                            id={"sconneZ_dropdown_" + index}
                            dataTestId={"dropdown_sconneZ"}
                            onUpdate={(value: { id: string } | null) => {
                              if (value === null) {
                                return;
                              }

                              if (!checkSupportTypes(value.id as SupportConnectivityType, "Z")) {
                                return;
                              }

                              if (sup.guid) {
                                updateSupportConnectivity(sup.guid, "Z", value.id as SupportConnectivityType);
                              }
                            }}
                            list={dropdownOptions}
                            val={getDropdownValue(sup, "Z")}
                            localisation={undefined}
                            lang={""}
                          />
                        </td>
                        {/* <td style={{ border: 0 }}>
                          <span
                            onClick={() => {
                              if (sup && sup.guid) {
                                _delSup(sup.guid);
                              }
                            }}
                            className="delete-row-btn"
                            id={"delSup_" + index}
                          >
                            <TimesIcon size={12} />
                          </span>
                        </td> */}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </Table>
            {/* Add row button below the table */}
            <Row>
              <div className="text-right" style={{ paddingRight: 10 }}>
                {/* <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 200, hide: 200 }}
                  overlay={
                    <Tooltip id="tooltip">{t("adding_multiple_supports_will_soon_be_available_.label")}</Tooltip>
                  }
                > 
                  <Button
                  style={{ pointerEvents: "all" }} // Ensure button is clickable even with overlay
                  className="addrow add-row-btn"
                  onClick={_addSup}
                  id="addNewSupport"
                  data-testid="addNewSupport"
                >
                  <PlusIcon size={16} />
                </Button> 
                 </OverlayTrigger> */}
              </div>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  );
};
